import React, { useContext, useLayoutEffect, useState } from 'react'
import { graphql, Link } from 'gatsby';
import { css } from '@emotion/react';
import { ThemeContext } from '../components/theme-provider';
import SEO from '../components/seo';
import ProgramRow from '../components/program-row';
import Accordion from '../components/accordion';

export default function WhatsOn({ data }) {
  const [programs, setPrograms] = useState([]);
  const [search, setSearch] = useState(null);
  const [date, setDate] = useState(null);
  const [dateOpen, setDateOpen] = useState(false);
  const [format, setFormat] = useState(null);
  const [formatOpen, setFormatOpen] = useState(false);
  const [location, setLocation] = useState('All');
  const [locationOpen, setLocationOpen] = useState(false);
  const [previousYears, setPreviousYears] = useState([]);
  const [background, setBackground] = useState(null);
  const [inverse, setInverse] = useState(null);

  const theme = useContext(ThemeContext);

  useLayoutEffect(() => {
    theme.setGrayFooter(true);

    if (window.location.hash) {
      setFormat(window.location.hash.slice(1));
      window.scrollTo(0, 0);
    }
  }, []);

  useLayoutEffect(() => {
    setPrograms(data.programs.nodes
      .filter((program) => program.startDate)
    );
    setPreviousYears(data.programs.nodes
      .filter((program) => program.startDate)
      .map((program) => new Date(program.startDate).getFullYear())
      .filter((program, i, programs) => programs.indexOf(program) === i)
    );
  }, [data.programs.nodes]);


  useLayoutEffect(() => {
    if (search) {
      const lowerSearch = search.toLowerCase();
      const newPrograms = data.programs.nodes.filter((program) => {
        if (program.format.map((format) => format.title).join(' ').toLowerCase().includes(lowerSearch)) return true;
        if (program.people.map((person) => person.name).join(' ').toLowerCase().includes(lowerSearch)) return true;
        if (program.title.toLowerCase().includes(lowerSearch)) return true;
      });

      filter(newPrograms);
    } else {
      filter(data.programs.nodes);
    }
  }, [search]);

  useLayoutEffect(() => {
    filter(data.programs.nodes);
  }, [date, format, location]);

  function filter(programs) {
    setPrograms(programs
      .filter((program) => {
        if (format) {
          if (program.format.map((format) => format.slug?.current).join(' ').includes(format)) return true;
        } else {
          return true;
        }
      })
      .filter((program) => {
        if (date !== null) {
          if (new Date(program.startDate).getMonth() === date || new Date(program.endDate).getMonth() === date) return true;
        } else {
          return true;
        }
      })
      .filter((program) => {
        if (location === 'All' || program.location?.includes(location)) return true;
      })
    );
  }

  function convertDate(date) {
    switch(date) {
      case 0:
        return 'January';
      case 1:
        return 'February';
      case 2:
        return 'March';
      case 3:
        return 'April';
      case 4:
        return 'May';
      case 5:
        return 'June';
      case 6:
        return 'July';
      case 7:
        return 'August';
      case 8:
        return 'September';
      case 9:
        return 'October';
      case 10:
        return 'November';
      case 11:
        return 'December';
    }
  }

  return (
    <div
      css={css`
        background-color: ${(background) ? background : 'var(--primary)'};
        transition: background-color 0.6s var(--curve);
      `}
    >
      <SEO
        title="What's On"
      />
      <div
        css={css`
          display: grid;
          grid-template-columns: repeat(12, 1fr);
          gap: var(--gutter);
        `}
      >
      <h1
        className="type--70"
        css={css`
          grid-column: span 10;
          color: ${(inverse) ? 'var(--white)' : 'var(--black)'};
          padding: var(--margin) var(--margin) 0;
          margin-bottom: var(--bottomMargin);

          @media (max-width: 1080px) {
            grid-column: span 12;
          }

          a {
            color: ${(inverse) ? 'var(--white)' : 'var(--black)'};
            text-decoration: none;

            &:hover {
              color: var(--white);
            }
          }
        `}
      ><Link to="/">The Substation</Link>/&#x200B;What's On/</h1>
      </div>

      <div
        css={css`
          padding: 0 var(--margin);
        `}
      >
        <div
          className="type--70"
          css={css`
            display: flex;
            flex-wrap: wrap;
            margin-bottom: var(--gutter);

            > div {
              flex: 0 0 calc(50% - (var(--gutter) / 2));
              align-self: flex-start; 
              background-color: var(--white);
              padding: 0 var(--gutter);
              border-top: 0;
              margin-bottom: var(--gutter);

              @media (max-width: 1080px) {
                flex: 0 0 100%;
                margin-right: 0;
              }

              &:nth-of-type(2n + 1) {
                margin-right: var(--gutter);
              }

              > div:first-of-type {
                > div:first-child {
                  flex-grow: 1;
                  position: relative;
                  max-height: 6.6rem;
                  word-break: break-all;
                  overflow: hidden;

                  &::after {
                    content: '';
                    position: absolute;
                    top: 0;
                    right: 0;
                    bottom: 0;
                    width: 10rem;
                    background-image: linear-gradient(to right, rgba(255, 255, 255, 0), var(--white));
                  }
                }
              }

              [role="button"] {
                cursor: pointer;
                transition: color 0.6s var(--curve);

                &:hover {
                  color: var(--gray);
                  transition: color 0s;
                }
              }
            }
          `}
        >
          <div
            css={css`
              position: relative;

              &::after {
                content: '';
                position: absolute;
                top: 0;
                right: 0;
                bottom: 0;
                width: 20rem;
                background-image: linear-gradient(to right, rgba(255, 255, 255, 0), var(--white), var(--white) 20rem);
                pointer-events: none;
              }
            `}
          >
            <input
              className="type--70"
              css={css`
                position: relative;
                top: 0.5rem;
                width: 100%;
                height: 10.6rem;
                background-color: transparent;
                border-radius: 0;
                -webkit-appearance: none;
                padding-left: 0;
                padding-right: 5rem;
                border: 0;

                @media (max-width: 1080px) {
                  height: 11rem;
                }

                @media (max-width: 700px) {
                  position: relative;
                  top: 0.6rem;
                  height: 9rem;
                }
              `}
              placeholder="Search..."
              onKeyUp={(e) => {
                e.persist();
               
                setSearch(e.target.value);
              }}
            />
            <img
              css={css`
                position: absolute;
                top: calc(var(--gutter) * 1.2);
                right: var(--gutter);
                width: auto;
                height: 4.8rem;
                z-index: 1;

                @media (max-width: 1080px) {
                  height: 4rem;
                }
              `}
              src="/images/glass.svg"
            />
          </div>
          <Accordion
            css={css`
              word-break: break-word;
            `}
            title={(format) ? format : 'All'}
            open={formatOpen}
            setOpen={setFormatOpen}
            noPadding={true}
          >
            <span>
              <span role="button" onClick={() => { setFormat(null); setFormatOpen(false); }}>All</span>,{' '} 
            </span>
            {data.formats.nodes.length > 0 && data.formats.nodes
              .map((format, i) => (
                <span>
                  <span key={i} role="button" onClick={() => { setFormat(format.slug?.current); setFormatOpen(false); }}>{format.title}</span>{(i === data.formats.nodes.length - 1) ? null : ', '}
                </span>
              ))
            }
          </Accordion>
          <Accordion
            title={(date !== null) ? convertDate(date) : 'All Dates'}
            open={dateOpen}
            setOpen={setDateOpen}
            noPadding={true}
          >
            <span role="button" onClick={() => { setDate(null); setDateOpen(false); }}>All</span>,{' '}
            <span role="button" onClick={() => { setDate(0); setDateOpen(false); }}>Jan</span>,{' '}
            <span role="button" onClick={() => { setDate(1); setDateOpen(false); }}>Feb</span>,{' '}
            <span role="button" onClick={() => { setDate(2); setDateOpen(false); }}>Mar</span>,{' '}
            <span role="button" onClick={() => { setDate(3); setDateOpen(false); }}>Apr</span>,{' '}
            <span role="button" onClick={() => { setDate(4); setDateOpen(false); }}>May</span>,{' '}
            <span role="button" onClick={() => { setDate(5); setDateOpen(false); }}>Jun</span>,{' '}
            <span role="button" onClick={() => { setDate(6); setDateOpen(false); }}>Jul</span>,{' '}
            <span role="button" onClick={() => { setDate(7); setDateOpen(false); }}>Aug</span>,{' '}
            <span role="button" onClick={() => { setDate(8); setDateOpen(false); }}>Sep</span>,{' '}
            <span role="button" onClick={() => { setDate(9); setDateOpen(false); }}>Oct</span>,{' '}
            <span role="button" onClick={() => { setDate(10); setDateOpen(false); }}>Nov</span>,{' '}
            <span role="button" onClick={() => { setDate(11); setDateOpen(false); }}>Dec</span>
          </Accordion>
          <Accordion
            title={location.replace('-', '')}
            open={locationOpen}
            setOpen={setLocationOpen}
            noPadding={true}
          >
            <div role="button" onClick={() => { setLocation('All'); setLocationOpen(false); setBackground(null); setInverse(false); }}>All</div>
            <div role="button" onClick={() => { setLocation('On-site'); setLocationOpen(false); setBackground(null); setInverse(false); }}>Onsite</div>
            <div role="button" onClick={() => { setLocation('Off-site'); setLocationOpen(false); setBackground('var(--peach)'); setInverse(false); }}>Offsite</div> <div role="button" onClick={() => { setLocation('Online'); setLocationOpen(false); setBackground('var(--black)'); setInverse(true); }}>Online</div>
          </Accordion>
        </div>
        <div
          css={css`
            margin-bottom: var(--margin);

            &, a {
              color: ${(inverse) ? 'var(--white)' : 'var(--black)'};
              border-color: ${(inverse) ? 'var(--white)' : 'var(--black)'};
              transition: color 0.6s var(--curve), border-color 0.6s var(--curve);
            }

            .svg {
              filter: invert(${(inverse) ? '100%' : 0});
              transition: filter 0.6s var(--curve);
            }

            div::before {
              background-color: ${(inverse) ? 'var(--white)' : 'var(--black)'};
              transition: background-color 0.6s var(--curve);
            }
          `}
        >
          {programs.filter((program) => new Date(program.startDate).getFullYear() === new Date().getFullYear() || new Date(program.endDate).getFullYear() === new Date().getFullYear()).length === 0 && <div className="type--45">
              Sorry, there are no {new Date().getFullYear()} programs that match your search or filters.
          </div>}
          {programs.length > 0 && programs
            .filter((program) => new Date(program.startDate).getFullYear() >= new Date().getFullYear() || new Date(program.endDate).getFullYear() >= new Date().getFullYear())
            .map((program, i) => (
              <ProgramRow key={i} program={program}/>
            ))
          }
        </div>
      </div>
      <div
        css={css`
          background-image: linear-gradient(to bottom, rgba(255, 255, 255, 0), var(--white) 10rem, var(--white) 100%);
          padding: 15rem var(--margin) var(--bigMargin);
        `}
      >
        <h2
          className="type--70"
          css={css`
            margin-bottom: var(--margin);
          `}
        >Archived Events/</h2>
        {previousYears.length > 0 && previousYears
          .filter((year) => year !== new Date().getFullYear())
          .map((year, i) => (
            <Accordion
              key={i}
              title={year}
              open={(programs.filter((program) => new Date(program.startDate).getFullYear() === year || new Date(program.endDate).getFullYear() === year).length > 0) ? true : false}
            >
              {programs.filter((program) => new Date(program.startDate).getFullYear() === year || new Date(program.endDate).getFullYear() === year).length === 0 && <div>
                  Sorry, there are no {year} programs that match your search or filters.
              </div>}
              {programs.length > 0 && programs
                .filter((program) => new Date(program.startDate).getFullYear() === year || new Date(program.endDate).getFullYear() === year)
                .map((program, i) => (
                  <ProgramRow key={i} program={program}/>
                ))
              }
            </Accordion>
          ))
        }
      </div>
    </div>
  );
}

export const query = graphql`
  query {
    programs: allSanityProgram(sort: {fields: startDate, order: DESC}, filter: {private: {ne: true}, inDevelopment: {ne: true}}) {
      nodes {
        id
        slug {
          current
        }
        format {
          title
          slug {
            current
          }
        }
        location
        featureImage {
          ...Image
        }
        people {
          name
          slug {
            current
          }
        }
        title
        startDate
        endDate
        _rawContent(resolveReferences: {maxDepth: 10})
      }
    }
    formats: allSanityProgramFormat(sort: {fields: title}) {
      nodes {
        id
        title
        slug {
          current
        }
      }
    }
  }
`

